import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios, { AxiosInstance, AxiosResponse } from "axios";
import { IdToken, useAuth0 } from "@auth0/auth0-react";
import { params } from "@/params";
import { FeatureAccessContext } from "@/context/featureAccess";
import { useTraceInterceptor } from "./interceptors/useTraceInterceptor";

const ApiContext = createContext<{
  api: AxiosInstance;
  tokenId?: IdToken;
  selectedWorkspace?: string;
} | null>(null);

function createAxiosInstance(
  baseURL: string,
  authToken?: string,
  tenantId?: string
): AxiosInstance {
  const instance = axios.create({
    baseURL,
    timeout: 120_000,
    headers: {
      "Content-Type": "application/json",
      ...(authToken != null && { Authorization: `Bearer ${authToken}` }),
      ...(tenantId && { "x-tenant-id": tenantId }),
    },
  });

  return instance;
}

export function ApiProvider({ children }: PropsWithChildren) {
  const { isAuthenticated, getIdTokenClaims } = useAuth0();
  const { selectedWorkspace } = useContext(FeatureAccessContext);
  const traceInterceptor = useTraceInterceptor();

  const [tokenId, setTokenId] = useState<IdToken>();

  const api = useMemo(() => {
    const authToken = tokenId?.__raw;

    const instance = createAxiosInstance(
      params.API_URL,
      authToken,
      selectedWorkspace
    );

    instance.interceptors.request.use(
      traceInterceptor.onRequest,
      (error) => Promise.reject(error)
    );

    instance.interceptors.response.use(
      traceInterceptor.onResponse,
      traceInterceptor.onResponseError
    );

    return instance;
  }, [tokenId, selectedWorkspace, traceInterceptor]);

  useEffect(() => {
    if (isAuthenticated) {
      (async () => {
        const tokenId = await getIdTokenClaims();
        setTokenId(tokenId);
      })();
    }
  }, [isAuthenticated, getIdTokenClaims]);

  return (
    <ApiContext.Provider value={{ api, tokenId, selectedWorkspace }}>
      {children}
    </ApiContext.Provider>
  );
}

export function useApi(): { api: AxiosInstance } {
  const contextValue = useContext(ApiContext);
  if (contextValue == null) {
    throw new Error("useApi must be used within an ApiProvider");
  }
  return { api: contextValue.api };
}

export function useCustomApi(baseURL: string): { api: AxiosInstance } {
  const contextValue = useContext(ApiContext);
  const traceInterceptor = useTraceInterceptor();

  if (contextValue == null) {
    throw new Error("useCustomApi must be used within an ApiProvider");
  }

  const { tokenId, selectedWorkspace } = contextValue;

  const api = useMemo(() => {
    const authToken = tokenId?.__raw;

    const instance = createAxiosInstance(baseURL, authToken, selectedWorkspace);

    instance.interceptors.request.use(
      traceInterceptor.onRequest,
      (error) => Promise.reject(error)
    );

    instance.interceptors.response.use(
      traceInterceptor.onResponse,
      traceInterceptor.onResponseError
    );

    return instance;
  }, [baseURL, tokenId, selectedWorkspace, traceInterceptor]);

  return { api };
}

export async function apiResolver<T>(response: Promise<AxiosResponse<T>>) {
  const { data } = await response;
  return data;
}
